import { useMutation, useQuery } from '@apollo/client';
import { FC, useState, useEffect } from 'react';
import {
  CERTIFICATE_ATTACHMENTS,
  CREATE_ATTACHMENT,
  GET_CERTIFICATE_DETAILS,
  REMOVE_CERTIFICATE_ATTACHMENT,
  UPDATE_MC1,
  UPDATE_SH1,
} from '../../../graph-queries/tar/certificate';
import { useNoticeBar } from '../../molecules/NoticeBar/NoticeBar';
import { MediaUploadRow, MediaUploadSelector } from '../MediaUpload/MediaUpload';
import { CertificateStatusEnum, MediaContext } from '../../../graph-queries/__generated__/graphql';
import FileUploadButton from './FileUploadButton';
import classes from './CertificateAttachments.module.scss';
import { useCertificateAttachments, useFileUpload } from './CertificateAttachments.util';
import SaveCancelButtons from './SaveCancelButtons';
import AttachmentsList from './AttachmentsList';
import MC1Attachment from '../../pages/CertificateMonitoring/MC1Attachment';
import { Label } from '@bphxd/ds-core-react';
import { useHasMaintenanceAdminAccess } from '../../../lib/util/auth';

const CertificateAttachments: FC<{ certificateId: number; onClose: () => void }> = ({
  certificateId,
  onClose,
}) => {
  const [toBeDeleted, setToBeDeleted] = useState<number[]>([]);
  // const [sh1ToBeDeleted, setSh1ToBeDeleted] = useState<number[]>([]);
  // const [mc1ToBeDeleted, setMc1ToBeDeleted] = useState<number[]>([]);
  const { sendSuccess, sendError } = useNoticeBar();
  const { attachments, loadingAttachments, showAll, setShowAll } =
    useCertificateAttachments(certificateId);
  const { data: certificateDetails, loading: certificateDetailsLoading } = useQuery(
    GET_CERTIFICATE_DETAILS,
    { variables: { certificateId } }
  );
  const [createAttachment] = useMutation(CREATE_ATTACHMENT, {
    refetchQueries: [{ query: CERTIFICATE_ATTACHMENTS, variables: { certificateId } }],
    awaitRefetchQueries: true,
  });
  const [removeAttachment] = useMutation(REMOVE_CERTIFICATE_ATTACHMENT, {
    refetchQueries: [{ query: CERTIFICATE_ATTACHMENTS, variables: { certificateId } }],
    awaitRefetchQueries: true,
  });
  // const [updateSh1, { loading:sh1Loading }] = useMutation(UPDATE_SH1, {
  //     refetchQueries: [{ query: GET_CERTIFICATE_DETAILS, variables: { certificateId } }],
  //     awaitRefetchQueries: true,
  //   });
  //   const [updateMc1, { loading:mc1Loading }] = useMutation(UPDATE_MC1, {
  //     refetchQueries: [{ query: GET_CERTIFICATE_DETAILS, variables: { certificateId } }],
  //     awaitRefetchQueries: true,
  //   });
  const hasMaintAdminAccess = useHasMaintenanceAdminAccess();
  const mc1 = certificateDetails?.certificateDetails.maintenanceMC1;
  const sh1 = certificateDetails?.certificateDetails.maintenanceSH1;
  const canDelete =
    certificateDetails?.certificateDetails.status === CertificateStatusEnum.Draft ||
    certificateDetails?.certificateDetails.status === CertificateStatusEnum.IssuedForReview ||
    hasMaintAdminAccess;

  const { state, dispatch, uploadFile, retry } = useFileUpload(MediaContext.Certificate);

  const handleUploadSuccess = (generated: { blobLocation: string; url: string }, file: File) => {
    return createAttachment({
      variables: {
        input: {
          certificateId,
          fileName: file.name,
          blobLocation: generated.blobLocation,
          mime: file.type,
        },
      },
    });
  };

  const onSaveClick = async () => {
    if (state.fileRows.length) {
      await Promise.all(state.fileRows.map((row) => uploadFile(row.file, handleUploadSuccess)))
        .then(() => {
          sendSuccess(
            `Your ${state.fileRows.length === 1 ? 'file has' : 'files have'} been uploaded successfully!`
          );
          dispatch({ type: 'reset' });
        })
        .catch(() => sendError('Upload failed. Remove invalid file to continue.'));
    }

    if (toBeDeleted.length) {
      await Promise.all(
        toBeDeleted.map((attachmentId) => removeAttachment({ variables: { attachmentId } }))
      )
        .then(() => {
          sendSuccess(
            `Your ${toBeDeleted.length === 1 ? 'file has' : 'files have'} been removed successfully!`
          );
          setToBeDeleted([]);
        })
        .catch(() => sendError('Remove failed.'));
    }
    // if (sh1ToBeDeleted.length) {
    //   await Promise.all(
    //     toBeDeleted.map((sh1Id) =>
    //       updateSh1({
    //         variables: {
    //           input: {
    //             sh1Id,
    //             mime: '',
    //             fileName: '',
    //             blobLocation: '',
    //           },
    //         },
    //       })
    //     )
    //   )
    //     .then(() => {
    //       sendSuccess(
    //         `Your ${sh1ToBeDeleted.length === 1 ? 'file has' : 'files have'} been removed successfully!`
    //       );
    //       setSh1ToBeDeleted([]);
    //     })
    //     .catch(() => sendError('Remove failed.'));
    // }
    // if (mc1ToBeDeleted.length) {
    //   await Promise.all(
    //     toBeDeleted.map((mc1Id) =>
    //       updateMc1({
    //         variables: {
    //           input: {
    //             mc1Id,
    //             mime: '',
    //             fileName: '',
    //             blobLocation: '',
    //           },
    //         },
    //       })
    //     )
    //   )
    //     .then(() => {
    //       sendSuccess(
    //         `Your ${mc1ToBeDeleted.length === 1 ? 'file has' : 'files have'} been removed successfully!`
    //       );
    //       setMc1ToBeDeleted([]);
    //     })
    //     .catch(() => sendError('Remove failed.'));
    // }
  };

  const mc1Attachments = [
    {
      id: mc1?.mc1Id || -1,
      url: mc1?.mediaUrl || '',
      fileName: mc1?.fileName || '',
      mime: mc1?.mime || '',
    },
  ];

  const sh1Attachments = [
    {
      id: sh1?.sh1Id || -1,
      url: sh1?.mediaUrl || '',
      fileName: sh1?.fileName || '',
      mime: sh1?.mime || '',
    },
  ];

  return (
    <div className={classes.root}>
      <div className="row">
        <div className="col-md-12">
          {!state.fileRows.length ? (
            <div className={classes.dropArea}>
              <MediaUploadSelector state={state} dispatch={dispatch} />
            </div>
          ) : (
            <div>
              <Label>Selected Files</Label>
              {state.fileRows.map((row) => (
                <MediaUploadRow
                  key={row.file.name}
                  row={row}
                  dispatch={dispatch}
                  onRetry={() => retry(row.file, handleUploadSuccess)}
                />
              ))}
            </div>
          )}
        </div>
      </div>
      <Label className="mt-6">Uploaded Files</Label>
      <AttachmentsList
        attachments={attachments}
        loading={loadingAttachments}
        toBeDeleted={toBeDeleted}
        onToggleDelete={(id) => {
          if (!canDelete) return;
          setToBeDeleted((list) =>
            list.includes(id) ? list.filter((i) => i !== id) : [...list, id]
          );
        }}
        showAll={showAll}
        onShowAll={() => setShowAll(true)}
      />
      <div className="d-flex gap-4">
        {mc1?.fileName && (
          <div>
            MC1
            <AttachmentsList
              attachments={mc1Attachments}
              showAll={showAll}
              onShowAll={() => setShowAll(true)}
            />
          </div>
        )}
        {sh1?.fileName && (
          <div>
            SH1
            <AttachmentsList
              attachments={sh1Attachments}
              showAll={showAll}
              onShowAll={() => setShowAll(true)}
            />
          </div>
        )}
      </div>
      <SaveCancelButtons
        onSave={onSaveClick}
        onCancel={onClose}
        saveDisabled={!state.fileRows.length && !toBeDeleted.length}
      />
    </div>
  );
};

export default CertificateAttachments;
